// Management Hub - Configuration Multi-tenant
// Gestion des Entreprises, Activités et Assets digitaux

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Building2, Briefcase, Globe, Facebook, Linkedin, FileText,
  Plus, Pencil, Trash2, ChevronRight, ChevronDown, Search,
  Loader2, Check, X, ExternalLink, RefreshCw, Target, Palette
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Asset type config
const ASSET_TYPES = {
  site_web: { icon: Globe, color: "text-blue-400", bg: "bg-blue-500/10", label: "Site Web" },
  landing_page: { icon: FileText, color: "text-purple-400", bg: "bg-purple-500/10", label: "Landing Page" },
  page_facebook: { icon: Facebook, color: "text-blue-500", bg: "bg-blue-600/10", label: "Page Facebook" },
  page_linkedin: { icon: Linkedin, color: "text-sky-400", bg: "bg-sky-500/10", label: "Page LinkedIn" }
};

const ASSET_STATUS = {
  actif: { color: "text-emerald-400", bg: "bg-emerald-500/20", label: "Actif" },
  inactif: { color: "text-zinc-400", bg: "bg-zinc-500/20", label: "Inactif" },
  en_configuration: { color: "text-amber-400", bg: "bg-amber-500/20", label: "En config" }
};

// Entreprise Card
const EntrepriseCard = ({ entreprise, isExpanded, onToggle, onEdit, onDelete, children }) => (
  <Card className="bg-zinc-900/50 border-white/10 overflow-hidden">
    <CardHeader 
      className="cursor-pointer hover:bg-zinc-800/30 transition-colors"
      onClick={onToggle}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <CardTitle className="text-white text-lg">{entreprise.nom}</CardTitle>
            <p className="text-sm text-zinc-400">{entreprise.description || "Aucune description"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <Badge className="bg-blue-500/20 text-blue-400 border-0">
              {entreprise.activites_count} activité(s)
            </Badge>
            <Badge className="bg-purple-500/20 text-purple-400 border-0">
              {entreprise.assets_count} asset(s)
            </Badge>
            <Badge className="bg-amber-500/20 text-amber-400 border-0">
              {entreprise.leads_count} lead(s)
            </Badge>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEdit(); }} aria-label="Modifier l'entreprise">
              <Pencil className="w-4 h-4 text-zinc-400" />
            </Button>
            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDelete(); }} aria-label="Supprimer l'entreprise">
              <Trash2 className="w-4 h-4 text-red-400" />
            </Button>
          </div>
          {isExpanded ? <ChevronDown className="w-5 h-5 text-zinc-500" /> : <ChevronRight className="w-5 h-5 text-zinc-500" />}
        </div>
      </div>
    </CardHeader>
    {isExpanded && <CardContent className="border-t border-white/5 pt-4">{children}</CardContent>}
  </Card>
);

// Activité Card
const ActiviteCard = ({ activité, isExpanded, onToggle, onEdit, onDelete, onAddAsset, children }) => (
  <div className="border border-white/10 rounded-lg overflow-hidden bg-zinc-800/30">
    <div 
      className="p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between"
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: activité.couleur_primaire ? `${activité.couleur_primaire}20` : '#3b82f620' }}
        >
          <Briefcase className="w-4 h-4" style={{ color: activité.couleur_primaire || 'var(--oh-stat-blue-text)' }} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-white">{activité.nom}</span>
            <Badge className="bg-zinc-700 text-zinc-300 border-0 text-xs">{activité.code}</Badge>
          </div>
          <p className="text-xs text-zinc-500">{activité.description || "Aucune description"}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex gap-2">
          <Badge className="bg-zinc-700/50 text-zinc-400 border-0 text-xs">
            {activité.assets_count} assets
          </Badge>
          <Badge className="bg-zinc-700/50 text-zinc-400 border-0 text-xs">
            {activité.leads_count} leads
          </Badge>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onAddAsset(); }} aria-label="Ajouter un asset">
            <Plus className="w-4 h-4 text-emerald-400" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onEdit(); }} aria-label="Modifier l'activité">
            <Pencil className="w-4 h-4 text-zinc-400" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onDelete(); }} aria-label="Supprimer l'activité">
            <Trash2 className="w-4 h-4 text-red-400" />
          </Button>
        </div>
        {isExpanded ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
      </div>
    </div>
    {isExpanded && <div className="border-t border-white/5 p-4 bg-zinc-900/30">{children}</div>}
  </div>
);

// Asset Row
const AssetRow = ({ asset, onEdit, onDelete }) => {
  const typeConfig = ASSET_TYPES[asset.type] || ASSET_TYPES.site_web;
  const statusConfig = ASSET_STATUS[asset.status] || ASSET_STATUS.en_configuration;
  const TypeIcon = typeConfig.icon;
  
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded ${typeConfig.bg} flex items-center justify-center`}>
          <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-white">{asset.nom}</span>
            <Badge className={`${statusConfig.bg} ${statusConfig.color} border-0 text-xs`}>
              {statusConfig.label}
            </Badge>
          </div>
          {asset.url && (
            <a 
              href={asset.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-zinc-500 hover:text-blue-400 flex items-center gap-1"
            >
              {asset.url} <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} aria-label="Modifier">
          <Pencil className="w-3 h-3 text-zinc-400" />
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDelete} aria-label="Supprimer">
          <Trash2 className="w-3 h-3 text-red-400" />
        </Button>
      </div>
    </div>
  );
};

export default function ConfigMultitenant() {
  const [entreprises, setEntreprises] = useState([]);
  const [activites, setActivites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // UI State
  const [expandedEntreprises, setExpandedEntreprises] = useState([]);
  const [expandedActivites, setExpandedActivites] = useState([]);
  const [search, setSearch] = useState("");
  
  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null); // 'entreprise', 'activité', 'asset'
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit'
  const [dialogData, setDialogData] = useState({});
  const [dialogParent, setDialogParent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [entRes, actRes, assRes] = await Promise.all([
        axios.get(`${API}/api/marketing/config/entreprises`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/marketing/config/activites`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/marketing/config/assets`, { headers: getAuthHeaders() })
      ]);
      setEntreprises(entRes.data);
      setActivites(actRes.data);
      setAssets(assRes.data);
      
      // Auto-expand first entreprise
      if (entRes.data.length > 0 && expandedEntreprises.length === 0) {
        setExpandedEntreprises([entRes.data[0].id]);
      }
    } catch (err) {
      console.error("Erreur chargement:", err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Toggle functions
  const toggleEntreprise = (id) => {
    setExpandedEntreprises(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };
  
  const toggleActivite = (id) => {
    setExpandedActivites(prev => 
      prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
    );
  };
  
  // Dialog handlers
  const openDialog = (type, mode = 'create', data = {}, parent = null) => {
    setDialogType(type);
    setDialogMode(mode);
    setDialogData(data);
    setDialogParent(parent);
    setDialogOpen(true);
  };
  
  const closeDialog = () => {
    setDialogOpen(false);
    setDialogType(null);
    setDialogData({});
    setDialogParent(null);
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const endpoint = dialogType === 'entreprise' ? 'entreprises' 
        : dialogType === 'activite' ? 'activites' 
        : 'assets';
      
      if (dialogMode === 'create') {
        await axios.post(
          `${API}/api/marketing/config/${endpoint}`,
          dialogData,
          { headers: getAuthHeaders() }
        );
      } else {
        await axios.patch(
          `${API}/api/marketing/config/${endpoint}/${dialogData.id}`,
          dialogData,
          { headers: getAuthHeaders() }
        );
      }
      
      closeDialog();
      loadData();
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      alert(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (type, id) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return;
    
    try {
      const endpoint = type === 'entreprise' ? 'entreprises' 
        : type === 'activite' ? 'activites' 
        : 'assets';
      
      await axios.delete(
        `${API}/api/marketing/config/${endpoint}/${id}`,
        { headers: getAuthHeaders() }
      );
      loadData();
    } catch (err) {
      console.error("Erreur suppression:", err);
      alert(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };
  
  // Get activites for an entreprise
  const getActivitesForEntreprise = (entrepriseId) => {
    return activites.filter(a => a.entreprise_id === entrepriseId);
  };
  
  // Get assets for an activité
  const getAssetsForActivite = (activiteId) => {
    return assets.filter(a => a.activite_id === activiteId);
  };
  
  // Render dialog content
  const renderDialogContent = () => {
    if (dialogType === 'entreprise') {
      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Nom de l'entreprise *</label>
            <Input
              value={dialogData.nom || ''}
              onChange={(e) => setDialogData({ ...dialogData, nom: e.target.value })}
              placeholder="Ex: AGENCE EXEMPLE"
              className="bg-zinc-800/50 border-zinc-700"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Description</label>
            <Input
              value={dialogData.description || ''}
              onChange={(e) => setDialogData({ ...dialogData, description: e.target.value })}
              placeholder="Description de l'entreprise"
              className="bg-zinc-800/50 border-zinc-700"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Email contact</label>
              <Input
                value={dialogData.email_contact || ''}
                onChange={(e) => setDialogData({ ...dialogData, email_contact: e.target.value })}
                placeholder="contact@entreprise.fr"
                className="bg-zinc-800/50 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Téléphone</label>
              <Input
                value={dialogData.telephone || ''}
                onChange={(e) => setDialogData({ ...dialogData, telephone: e.target.value })}
                placeholder="01 23 45 67 89"
                className="bg-zinc-800/50 border-zinc-700"
              />
            </div>
          </div>
        </div>
      );
    }
    
    if (dialogType === 'activite') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Nom de l'activité *</label>
              <Input
                value={dialogData.nom || ''}
                onChange={(e) => setDialogData({ ...dialogData, nom: e.target.value })}
                placeholder="Ex: Transition Énergétique France"
                className="bg-zinc-800/50 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Code court *</label>
              <Input
                value={dialogData.code || ''}
                onChange={(e) => setDialogData({ ...dialogData, code: e.target.value.toUpperCase() })}
                placeholder="TEF"
                className="bg-zinc-800/50 border-zinc-700"
                maxLength={10}
              />
            </div>
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Description</label>
            <Input
              value={dialogData.description || ''}
              onChange={(e) => setDialogData({ ...dialogData, description: e.target.value })}
              placeholder="Description de l'activité"
              className="bg-zinc-800/50 border-zinc-700"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Ligne éditoriale</label>
            <textarea
              value={dialogData.ligne_editoriale || ''}
              onChange={(e) => setDialogData({ ...dialogData, ligne_editoriale: e.target.value })}
              placeholder="Ton et style de communication..."
              className="w-full px-3 py-2 rounded-md bg-zinc-800/50 border border-zinc-700 text-white placeholder:text-zinc-500 text-sm"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Couleur primaire</label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={dialogData.couleur_primaire || 'var(--oh-stat-blue-text)'}
                  onChange={(e) => setDialogData({ ...dialogData, couleur_primaire: e.target.value })}
                  className="w-12 h-10 p-1 bg-zinc-800/50 border-zinc-700"
                />
                <Input
                  value={dialogData.couleur_primaire || 'var(--oh-stat-blue-text)'}
                  onChange={(e) => setDialogData({ ...dialogData, couleur_primaire: e.target.value })}
                  className="flex-1 bg-zinc-800/50 border-zinc-700"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Ton communication</label>
              <select
                value={dialogData.ton_communication || 'professionnel'}
                onChange={(e) => setDialogData({ ...dialogData, ton_communication: e.target.value })}
                className="w-full h-10 px-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-white"
              >
                <option value="professionnel">Professionnel</option>
                <option value="convivial">Convivial</option>
                <option value="expert">Expert</option>
                <option value="decontracte">Décontracté</option>
              </select>
            </div>
          </div>
        </div>
      );
    }
    
    if (dialogType === 'asset') {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Type d'asset *</label>
              <select
                value={dialogData.type || 'site_web'}
                onChange={(e) => setDialogData({ ...dialogData, type: e.target.value })}
                className="w-full h-10 px-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-white"
              >
                {Object.entries(ASSET_TYPES).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Statut</label>
              <select
                value={dialogData.status || 'en_configuration'}
                onChange={(e) => setDialogData({ ...dialogData, status: e.target.value })}
                className="w-full h-10 px-3 rounded-md bg-zinc-800/50 border border-zinc-700 text-white"
              >
                {Object.entries(ASSET_STATUS).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Nom de l'asset *</label>
            <Input
              value={dialogData.nom || ''}
              onChange={(e) => setDialogData({ ...dialogData, nom: e.target.value })}
              placeholder="Ex: Site vitrine"
              className="bg-zinc-800/50 border-zinc-700"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">URL</label>
            <Input
              value={dialogData.url || ''}
              onChange={(e) => setDialogData({ ...dialogData, url: e.target.value })}
              placeholder="https://..."
              className="bg-zinc-800/50 border-zinc-700"
            />
          </div>
          <div>
            <label className="text-sm text-zinc-400 mb-1 block">Description</label>
            <Input
              value={dialogData.description || ''}
              onChange={(e) => setDialogData({ ...dialogData, description: e.target.value })}
              placeholder="Description de l'asset"
              className="bg-zinc-800/50 border-zinc-700"
            />
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="config-multitenant">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-emerald-400" />
            Configuration Multi-tenant
          </h1>
          <p className="text-zinc-400">Gérez vos entreprises, activités et assets digitaux</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button 
            onClick={() => openDialog('entreprise', 'create', {})}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle entreprise
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{entreprises.length}</p>
              <p className="text-sm text-zinc-400">Entreprise(s)</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{activites.length}</p>
              <p className="text-sm text-zinc-400">Activité(s)</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{assets.length}</p>
              <p className="text-sm text-zinc-400">Asset(s) digital(aux)</p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Entreprises List */}
      <div className="space-y-4">
        {entreprises.length === 0 ? (
          <Card className="bg-zinc-900/50 border-white/10">
            <CardContent className="p-12 text-center">
              <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">Aucune entreprise configurée</p>
              <Button 
                onClick={() => openDialog('entreprise', 'create', {})}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer votre première entreprise
              </Button>
            </CardContent>
          </Card>
        ) : (
          entreprises.map(entreprise => (
            <EntrepriseCard
              key={entreprise.id}
              entreprise={entreprise}
              isExpanded={expandedEntreprises.includes(entreprise.id)}
              onToggle={() => toggleEntreprise(entreprise.id)}
              onEdit={() => openDialog('entreprise', 'edit', entreprise)}
              onDelete={() => handleDelete('entreprise', entreprise.id)}
            >
              {/* Activités de cette entreprise */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-zinc-400">Activités</h4>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openDialog('activité', 'create', { entreprise_id: entreprise.id })}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter
                  </Button>
                </div>
                
                {getActivitesForEntreprise(entreprise.id).length === 0 ? (
                  <p className="text-sm text-zinc-500 italic">Aucune activité</p>
                ) : (
                  <div className="space-y-2">
                    {getActivitesForEntreprise(entreprise.id).map(activité => (
                      <ActiviteCard
                        key={activité.id}
                        activité={activité}
                        isExpanded={expandedActivites.includes(activité.id)}
                        onToggle={() => toggleActivite(activité.id)}
                        onEdit={() => openDialog('activité', 'edit', activité)}
                        onDelete={() => handleDelete('activité', activité.id)}
                        onAddAsset={() => openDialog('asset', 'create', { activite_id: activité.id })}
                      >
                        {/* Assets de cette activité */}
                        <div className="space-y-2">
                          <h5 className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Assets digitaux</h5>
                          {getAssetsForActivite(activité.id).length === 0 ? (
                            <p className="text-sm text-zinc-500 italic">Aucun asset</p>
                          ) : (
                            <div className="space-y-2">
                              {getAssetsForActivite(activité.id).map(asset => (
                                <AssetRow
                                  key={asset.id}
                                  asset={asset}
                                  onEdit={() => openDialog('asset', 'edit', asset)}
                                  onDelete={() => handleDelete('asset', asset.id)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </ActiviteCard>
                    ))}
                  </div>
                )}
              </div>
            </EntrepriseCard>
          ))
        )}
      </div>
      
      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {dialogMode === 'create' ? 'Créer' : 'Modifier'}{' '}
              {dialogType === 'entreprise' ? 'une entreprise' : dialogType === 'activite' ? 'une activité' : 'un asset'}
            </DialogTitle>
          </DialogHeader>
          
          {renderDialogContent()}
          
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Annuler</Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {dialogMode === 'create' ? 'Créer' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
